import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import ServiceForm from './ServiceForm.js';
import { BookerContext } from '../Booker/index.js';
import { useIntl } from 'react-intl';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { getMomentDate } from '../../utils/momentUtils.js';
import classNames from 'classnames';
const Col = ({ children }) => {
    const { useTailwindPrefix } = useContext(BookerContext);
    return (_jsx("div", Object.assign({ className: classNames({
            'pr-4': !useTailwindPrefix,
            'tw-pr-4': useTailwindPrefix,
        }) }, { children: children })));
};
Col.Title = ({ children }) => {
    const { useTailwindPrefix } = useContext(BookerContext);
    return (_jsx("div", Object.assign({ className: classNames({
            ' text-[85%] text-zinc-400': !useTailwindPrefix,
            'tw-text-[85%] tw-text-zinc-400': useTailwindPrefix,
        }) }, { children: children })));
};
export const ServiceBooker = ({ showResume, serviceForm, isLoading, setResume, showClearDate = true }) => {
    var _a;
    const { locale, dateFormat, useTailwindPrefix } = useContext(BookerContext);
    const { date, startDate, endDate, guests, isRangeDate, hasQuantity, quantity, hasNumberPersons } = serviceForm;
    const getDefaultValues = useCallback(() => {
        var _a, _b, _c;
        return (Object.assign(Object.assign(Object.assign({}, (isRangeDate
            ? {
                dates: {
                    startDate: (_a = getMomentDate(startDate, locale)) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD'),
                    endDate: (_b = getMomentDate(endDate, locale)) === null || _b === void 0 ? void 0 : _b.format('YYYY-MM-DD'),
                },
            }
            : {
                date: (_c = getMomentDate(date, locale)) === null || _c === void 0 ? void 0 : _c.format('YYYY-MM-DD'),
            })), (hasNumberPersons && {
            guests: guests ? Object.assign({}, guests) : { adults: 2, children: 0, childrenAges: [] },
        })), (hasQuantity && {
            quantity: quantity || 0,
        })));
    }, []);
    const methods = useForm({
        defaultValues: getDefaultValues(),
    });
    const intl = useIntl();
    function getProps() {
        return Object.assign(Object.assign(Object.assign({ childrenAgesSelectorProps: {
                minChildrenAge: serviceForm.minChildrenAge || 0,
                maxChildrenAge: serviceForm.maxChildrenAge || 12,
            }, roomsSelectorProps: {
                maxRooms: 1,
                maxAdults: serviceForm.maxAdults || 8,
                maxChildren: serviceForm.maxChildren || 0,
                noChildrenAges: serviceForm.noChildrenAges,
            }, datePickerProps: Object.assign({ placeholder: intl.formatMessage({ id: 'booker.dayPass.date' }), displayFormat: dateFormat, id: serviceForm.dateId || 'date', showDefaultInputIcon: true, showClearDate }, (serviceForm.allotmentDates.length > 0 && {
                isDayBlocked: (day) => {
                    return !serviceForm.allotmentDates.some((date) => day.isSame(date, 'day'));
                },
            })) }, (serviceForm.isRangeDate && {
            isRangeDate: true,
            dateRangePickerProps: {
                startDatePlaceholderText: intl.formatMessage({
                    id: 'booker.startDate',
                }),
                endDatePlaceholderText: intl.formatMessage({
                    id: 'booker.endDate',
                }),
                startDateId: serviceForm.startDateId || 'startDateId',
                endDateId: serviceForm.endDateId || 'endDateId',
                showClearDates: true,
                showDefaultInputIcon: true,
                displayFormat: dateFormat,
            },
        })), (serviceForm.hasQuantity && {
            hasQuantity: true,
            maxQuantity: serviceForm.maxQuantity,
            minQuantity: serviceForm.minQuantity,
        })), { hasNumberPersons: serviceForm.hasNumberPersons, isLoading });
    }
    function onSubmitEvent(data) {
        serviceForm.onSubmit && serviceForm.onSubmit(data);
        setResume(true);
    }
    function hideResume(event) {
        event.preventDefault();
        setResume(false);
    }
    function formatDate(startDateString, endDateString) {
        const startDate = getMomentDate(startDateString, locale);
        const endDate = getMomentDate(endDateString, locale);
        if (!startDate || !endDate) {
            return null;
        }
        const isSameYear = startDate.isSame(endDate, 'year');
        const isSameMonth = startDate.isSame(endDate, 'month');
        let result = '';
        if (isSameYear) {
            if (isSameMonth) {
                result = `${startDate.format(intl.formatMessage({ id: 'booker.resume.date.day.format' }))} ${intl.formatMessage({ id: 'booker.to' })} ${endDate.format(intl.formatMessage({ id: 'booker.resume.date.format' }))}`;
            }
            else {
                result = `${startDate.format(intl.formatMessage({ id: 'booker.resume.date.month.format' }))} ${intl.formatMessage({ id: 'booker.to' })} ${endDate.format(intl.formatMessage({ id: 'booker.resume.date.format' }))}`;
            }
        }
        else {
            result = `${startDate.format(intl.formatMessage({ id: 'booker.resume.date.format' }))} ${intl.formatMessage({
                id: 'booker.to',
            })} ${endDate.format(intl.formatMessage({ id: 'booker.resume.date.format' }))}`;
        }
        return result.replace(/\./g, '');
    }
    const formValues = useWatch({ control: methods.control });
    return (_jsxs(FormProvider, Object.assign({}, methods, { children: [showResume && (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: classNames({
                            'flex justify-between ': !useTailwindPrefix,
                            'tw-flex tw-justify-between ': useTailwindPrefix,
                        }) }, { children: [_jsx(Col, { children: serviceForm.isRangeDate ? (_jsxs(_Fragment, { children: [_jsx(Col.Title, { children: intl.formatMessage({
                                                id: 'booker.dayPass.dates',
                                            }) }), formValues.dates && formatDate(formValues.dates.startDate, formValues.dates.endDate)] })) : (_jsxs(_Fragment, { children: [_jsx(Col.Title, { children: intl.formatMessage({
                                                id: 'booker.dayPass.date',
                                            }) }), formValues.date &&
                                            ((_a = getMomentDate(formValues.date)) === null || _a === void 0 ? void 0 : _a.format(intl.formatMessage({
                                                id: 'booker.resume.date.format',
                                            })))] })) }), serviceForm.hasNumberPersons && formValues.guests && (_jsxs(_Fragment, { children: [_jsxs(Col, { children: [_jsx(Col.Title, { children: intl.formatMessage({
                                                    id: serviceForm.maxChildren === 0 ? 'booker.persons' : 'booker.adults',
                                                }) }), formValues.guests.adults] }), serviceForm.maxChildren !== 0 && (_jsxs(Col, { children: [_jsx(Col.Title, { children: intl.formatMessage({
                                                    id: 'booker.minors',
                                                }) }), formValues.guests.children] }))] })), serviceForm.hasQuantity && formValues.quantity && (_jsxs(Col, { children: [_jsx(Col.Title, { children: intl.formatMessage({ id: 'booker.quantity' }) }), formValues.quantity] }))] })), _jsx("small", Object.assign({ onClick: hideResume, className: classNames({
                            'text-link-blue cursor-pointer': !useTailwindPrefix,
                            'tw-text-link-blue tw-cursor-pointer': useTailwindPrefix,
                        }) }, { children: intl.formatMessage({ id: 'booker.change' }) }))] })), !showResume && (_jsx("form", Object.assign({ onSubmit: methods.handleSubmit(onSubmitEvent) }, { children: _jsx(ServiceForm, Object.assign({}, getProps())) })))] })));
};
